import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code2, Zap, Globe2, Lightbulb, Search, Palette, Wrench, Rocket, HeartPulse, GraduationCap, TrendingUp, Building2, ArrowRight } from 'lucide-react';
import EmberParticles from '@/components/EmberParticles';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  })
};

const services = [
  { icon: Code2, title: 'Digital Systems Development', desc: 'Custom-built platforms, dashboards, and web applications tailored to your business.' },
  { icon: Zap, title: 'Business Process Automation', desc: 'Eliminate repetitive tasks and improve efficiency with smart, automated workflows.' },
  { icon: Globe2, title: 'Web Platforms & Portals', desc: 'User-friendly systems designed for clients, teams, and customers.' },
  { icon: Lightbulb, title: 'Consulting & Strategy', desc: 'Expert guidance to help you plan, design, and scale your digital systems.' },
];

const steps = [
  { icon: Search, title: 'Discover', desc: 'We understand your business, challenges, and goals.' },
  { icon: Palette, title: 'Design', desc: 'We map out a smart, scalable system tailored to your needs.' },
  { icon: Wrench, title: 'Develop', desc: 'We build your solution using modern, reliable technologies.' },
  { icon: Rocket, title: 'Deploy & Scale', desc: 'We launch, optimise, and support your system as you grow.' },
];

const industries = [
  { icon: HeartPulse, title: 'Healthcare', desc: 'Streamlining patient management, bookings, and operational workflows.' },
  { icon: GraduationCap, title: 'Education (EdTech)', desc: 'Building scalable learning platforms and student management systems.' },
  { icon: TrendingUp, title: 'Fintech', desc: 'Developing secure dashboards, reporting tools, and financial systems.' },
  { icon: Building2, title: 'Property Management', desc: 'Creating platforms for tenant management, payments, and operations.' },
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* ===== HERO ===== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/9003f248-e35e-4fe8-a9ec-ff2fb801281b/images/65b357be66ea46ee2191145c8e82cb817b4323a9a2edef49d5a8e488856dbef2.png)` }}
        />
        <div className="absolute inset-0 bg-[#0A0C1C]/75" />
        <EmberParticles />

        {/* Radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#E65A14]/8 rounded-full blur-[150px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-6"
            >
              Software &middot; Automation &middot; Innovation
            </motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.1]"
            >
              Igniting Intelligent{' '}
              <span className="bg-gradient-to-r from-[#E65A14] via-[#FF8C28] to-[#FFBE50] bg-clip-text text-transparent">
                Digital Systems
              </span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="mt-6 text-base sm:text-lg text-[#C8C8C8] leading-relaxed max-w-2xl"
            >
              We design and build powerful software, web applications, and automation systems that help healthcare providers, education platforms, fintech businesses, and property companies scale with confidence.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-get-started-btn"
                className="inline-flex items-center px-8 py-3.5 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_30px_rgba(230,90,20,0.5)] transition-all duration-300"
              >
                Get Started
                <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-consultation-btn"
                className="inline-flex items-center px-8 py-3.5 text-sm font-bold tracking-wide text-[#FF8C28] border border-[#E65A14]/50 rounded-sm hover:bg-[#E65A14]/10 transition-all duration-300"
              >
                Book a Free Consultation
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0C1C] to-transparent" />
      </section>

      {/* ===== VALUE PROPOSITION ===== */}
      <section data-testid="value-proposition-section" className="relative py-24 lg:py-32 bg-[#0A0C1C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
            >
              <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
                Why EmberCore
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
                Built for Performance.{' '}
                <span className="bg-gradient-to-r from-[#E65A14] to-[#FFBE50] bg-clip-text text-transparent">
                  Designed for Growth.
                </span>
              </motion.h2>
              <motion.div variants={fadeUp} custom={2} className="mt-6 space-y-4 text-[#C8C8C8] leading-relaxed">
                <p>
                  At EmberCore Digital, we go beyond websites. We create high-performance digital systems that streamline operations, eliminate manual work, and unlock scalable growth.
                </p>
                <p>
                  Whether you're managing patients, students, financial data, or property portfolios — our solutions are built to work seamlessly and grow with you.
                </p>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E65A14]/20 to-[#FF8C28]/10 rounded-2xl blur-2xl" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9003f248-e35e-4fe8-a9ec-ff2fb801281b/images/1ee5b2494c6410a1d342f24a1dec08defb4ef338e20b55757b7f4ad9d32cf348.png"
                alt="Premium workspace"
                className="relative rounded-lg border border-[#FF8C28]/20 w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section data-testid="services-section" className="relative py-24 lg:py-32 bg-gradient-to-b from-[#0A0C1C] to-[#12142B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              What We Do
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              Services Overview
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                custom={i}
                variants={fadeUp}
                data-testid={`service-card-${i}`}
                className="relative overflow-hidden bg-[#12142B] border border-[#FF8C28]/15 rounded-lg p-8 hover:-translate-y-1 hover:border-[#FF8C28]/50 hover:shadow-[0_0_30px_rgba(230,90,20,0.1)] transition-all duration-300 group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#E65A14]/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#E65A14]/20 to-[#FF8C28]/10 flex items-center justify-center mb-5">
                  <s.icon size={22} className="text-[#FF8C28]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{s.title}</h3>
                <p className="text-sm text-[#C8C8C8] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section data-testid="process-section" className="relative py-24 lg:py-32 bg-[#12142B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              Our Process
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              How We Work
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                data-testid={`process-step-${i}`}
                className="text-center group"
              >
                <div className="relative mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#E65A14]/20 to-transparent border border-[#FF8C28]/20 flex items-center justify-center mb-6 group-hover:border-[#FF8C28]/60 group-hover:shadow-[0_0_20px_rgba(230,90,20,0.2)] transition-all duration-300">
                  <step.icon size={28} className="text-[#FF8C28]" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-r from-[#E65A14] to-[#FF8C28] flex items-center justify-center text-xs font-bold text-white">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-[#C8C8C8] leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES ===== */}
      <section data-testid="industries-section" className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/9003f248-e35e-4fe8-a9ec-ff2fb801281b/images/7e310cbbba1436184b8111b5990c195f7183273a8d278897e447c99f8640c83d.png)` }}
        />
        <div className="absolute inset-0 bg-[#0A0C1C]/90" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              Industries
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              Who We Serve
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <motion.div
                key={ind.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                data-testid={`industry-card-${i}`}
                className="bg-[#12142B]/80 backdrop-blur-sm border border-[#FF8C28]/15 rounded-lg p-6 hover:border-[#FF8C28]/40 hover:shadow-[0_0_25px_rgba(230,90,20,0.08)] transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#B42314]/20 to-[#E65A14]/10 flex items-center justify-center mb-4 group-hover:from-[#B42314]/30 group-hover:to-[#E65A14]/20 transition-all">
                  <ind.icon size={22} className="text-[#FF8C28]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{ind.title}</h3>
                <p className="text-sm text-[#C8C8C8] leading-relaxed">{ind.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section data-testid="cta-section" className="relative py-24 lg:py-32 bg-[#12142B]">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[600px] bg-[#E65A14]/5 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h2
              variants={fadeUp}
              custom={0}
              className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white"
            >
              Ready to power your business with{' '}
              <span className="bg-gradient-to-r from-[#E65A14] to-[#FFBE50] bg-clip-text text-transparent">
                smarter systems?
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="mt-5 text-[#C8C8C8] leading-relaxed">
              Let's build something that works harder for you.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="mt-10">
              <Link
                to="/contact"
                data-testid="cta-consultation-btn"
                className="inline-flex items-center px-10 py-4 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_40px_rgba(230,90,20,0.5)] transition-all duration-300"
              >
                Book Your Free Consultation
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
